package com.schoolconnect.app.feature.classes

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.designsystem.AppSearchableDropdownField
import com.schoolconnect.app.designsystem.list.AdminListMessageAndFilters
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListPageScaffold
import com.schoolconnect.app.designsystem.list.AdminListPrimaryActionButton
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListTableShimmer
import com.schoolconnect.app.designsystem.list.AdminListToolbar
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.ban
import ischoolconnect.sharedui.generated.resources.circle_check
import ischoolconnect.sharedui.generated.resources.filter
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.plus
import org.jetbrains.compose.resources.painterResource
import androidx.compose.foundation.layout.width

@Composable
fun ClassesScreen(
    state: ClassesUiState,
    onEvent: (ClassesUiEvent) -> Unit,
    onBack: () -> Unit,
    shellRoleCode: String = "",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {}
) {
    val page = classesPage(state)
    val classScopeOptions = classSectionScopeOptions(state.classSectionFilterOptions)
    val classSectionPickerOptions = classScopeOptions.filter {
        it.className.isNotBlank() && it.sectionName.isNotBlank()
    }
    val selectedClassScope = selectedClassSectionScope(state.filters, classScopeOptions)
    val filterDraftClassScope = selectedClassSectionScope(state.filterDraft, classScopeOptions)
    var rowsPerPageExpanded by remember { mutableStateOf(false) }
    var openActionMenuId by remember { mutableStateOf<String?>(null) }
    var accountMenuExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(page.items) {
        if (openActionMenuId != null && page.items.none { it.id == openActionMenuId }) {
            openActionMenuId = null
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        AdminListPageScaffold(
            title = "Manage Classes",
            subtitle = "Create, update and manage class and section mappings.",
            headerActions = {
                ShellTopRightActions(
                    roleLabel = shellRoleLabel(shellRoleCode),
                    roleSubtitle = shellRoleSubtitle(shellRoleCode),
                    initials = shellAvatarInitials(shellRoleLabel(shellRoleCode)),
                    expanded = accountMenuExpanded,
                    onExpandedChange = { accountMenuExpanded = it },
                    onChangeRole = onChangeRole,
                    onLogout = onLogout
                )
            },
            backgroundColor = ClassesBackground,
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            summaryCards = {},
            showSummaryCards = false,
            bannerAfterToolbar = true,
            banner = {
                val filters = classFilterLabels(state.filters)
                if (state.dialogMode != ClassesDialogMode.EDITOR) {
                    AdminListMessageAndFilters(
                        message = state.errorMessage.ifBlank { state.infoMessage },
                        onDismissMessage = { onEvent(ClassesUiEvent.ClearMessages) },
                        chips = filters,
                        onClearAll = { onEvent(ClassesUiEvent.FilterReset) },
                        isError = state.errorMessage.isNotBlank(),
                        onRemoveChip = { chip ->
                            val next = when {
                                chip.startsWith("Class / Section:") -> state.filters.copy(classId = "", sectionId = "")
                                chip.startsWith("Subject code:") -> state.filters.copy(subjectCode = "")
                                chip.startsWith("Created:") -> state.filters.copy(createdFrom = "", createdTo = "")
                                else -> state.filters
                            }
                            onEvent(ClassesUiEvent.FilterApplied(next))
                        },
                        onEditFilters = { onEvent(ClassesUiEvent.FilterClicked) },
                        filterBackgroundColor = Color(0xFFF2F7FF),
                        filterBorderColor = ClassesBorder,
                        filterContentColor = ClassesNavy,
                        filterAccentColor = ClassesPrimary
                    )
                }
            },
            toolbar = {
                AdminListToolbar(
                    searchQuery = state.searchQuery,
                    onSearchQueryChanged = { onEvent(ClassesUiEvent.SearchChanged(it)) },
                    searchPlaceholder = "Search by class, section or subject code...",
                    searchWidth = 370.dp,
                    surfaceColor = ClassesCard,
                    borderColor = ClassesBorder,
                    searchIconTint = ClassesMuted,
                    searchTextColor = ClassesNavy,
                    searchCursorColor = ClassesPrimary,
                    inlineContent = {
                        AppSearchableDropdownField(
                            label = "",
                            value = selectedClassScope
                                .takeIf { it.className.isNotBlank() && it.sectionName.isNotBlank() }
                                ?.label
                                .orEmpty(),
                            placeholder = "Select Class / Section",
                            options = classSectionPickerOptions.map { it.label },
                            onValueChange = { selectedLabel ->
                                classSectionPickerOptions.firstOrNull { it.label == selectedLabel }?.let { scope ->
                                    onEvent(
                                        ClassesUiEvent.FilterApplied(
                                            state.filters.copy(classId = scope.className, sectionId = scope.sectionName)
                                        )
                                    )
                                }
                            },
                            modifier = Modifier.width(240.dp),
                            valueFontWeight = androidx.compose.ui.text.font.FontWeight.Normal,
                            maxMenuHeight = 320.dp
                        )
                    }
                ) {
                    AdminListToolbarButton(
                        label = "More Filters",
                        icon = painterResource(Res.drawable.filter),
                        onClick = { onEvent(ClassesUiEvent.FilterClicked) },
                        width = 150.dp,
                        borderColor = ClassesBorder,
                        contentColor = ClassesPrimary,
                        iconSize = 18.dp,
                        surfaceColor = ClassesCard
                    )
                    AdminListToolbarButton(
                        label = "Clear",
                        icon = painterResource(Res.drawable.close_x),
                        onClick = { onEvent(ClassesUiEvent.FilterReset) },
                        width = 100.dp,
                        borderColor = ClassesBorder,
                        contentColor = ClassesPrimary,
                        iconSize = 18.dp,
                        surfaceColor = ClassesCard
                    )
                }
            },
            table = { tableModifier ->
                    AdminListTableCard(
                    modifier = tableModifier,
                    minContentWidth = 760.dp,
                    surfaceColor = ClassesCard,
                    borderColor = ClassesBorder,
                    header = {
                        ClassesTableHeader(
                            sortField = state.sortField,
                            sortAscending = state.sortAscending,
                            onSortChanged = { onEvent(ClassesUiEvent.SortChanged(it)) }
                        )
                    },
                    body = {
                        if (state.isLoading) {
                            AdminListTableShimmer(
                                columns = classesTableShimmerColumns(),
                                rowCount = state.paging.pageSize.coerceAtLeast(5),
                                surfaceColor = ClassesCard,
                                borderColor = ClassesBorder
                            )
                        } else if (page.items.isEmpty()) {
                            ClassesTableEmptyState(modifier = Modifier.fillMaxWidth())
                        } else {
                            ClassesTableBody(
                                items = page.items,
                                openActionMenuId = openActionMenuId,
                                onActionMenuToggle = { classId ->
                                    openActionMenuId = if (openActionMenuId == classId) null else classId
                                },
                                onActionMenuDismiss = { classId ->
                                    if (openActionMenuId == classId) {
                                        openActionMenuId = null
                                    }
                                },
                                onViewDetails = {
                                    openActionMenuId = null
                                    onEvent(ClassesUiEvent.ViewClicked(it))
                                },
                                onEdit = {
                                    openActionMenuId = null
                                    onEvent(ClassesUiEvent.EditClicked(it))
                                },
                                onDelete = {
                                    openActionMenuId = null
                                    onEvent(ClassesUiEvent.DeleteClicked(it))
                                }
                            )
                        }
                    },
                    footer = {
                        AdminListFooter(
                            pageText = if (page.totalCount == 0) {
                                "Showing 0 to 0 of 0 classes"
                            } else {
                                "Showing ${page.startIndex} to ${page.endIndex} of ${page.totalCount} classes"
                            },
                            pageIndex = state.paging.pageIndex.coerceAtMost(page.pageCount - 1),
                            pageCount = page.pageCount,
                            onPageChanged = { onEvent(ClassesUiEvent.PageChanged(it)) },
                            pageSize = state.paging.pageSize,
                            rowsPerPageExpanded = rowsPerPageExpanded,
                            onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
                            onRowsPerPageDismiss = { rowsPerPageExpanded = false },
                            onPageSizeChanged = {
                                onEvent(ClassesUiEvent.PageSizeChanged(it))
                                rowsPerPageExpanded = false
                            },
                            footerColor = ClassesMuted,
                            borderColor = ClassesBorder,
                            surfaceColor = ClassesCard,
                            primaryColor = ClassesPrimary,
                            navyColor = ClassesNavy,
                            mutedColor = ClassesMuted
                        )
                    }
                )
            },
            bottomAction = {
                AdminListPrimaryActionButton(
                    label = "Add Class",
                    icon = painterResource(Res.drawable.plus),
                    onClick = { onEvent(ClassesUiEvent.AddClicked) },
                    modifier = Modifier.padding(top = 4.dp)
                )
            }
        )

        when (state.dialogMode) {
            ClassesDialogMode.FILTER -> ClassesFilterDialog(
                draft = state.filterDraft,
                classSectionValue = filterDraftClassScope.label,
                classSectionOptions = classScopeOptions,
                isSaving = state.isSaving,
                onDismiss = { onEvent(ClassesUiEvent.FilterDismissed) },
                onReset = { onEvent(ClassesUiEvent.FilterReset) },
                onSave = { filters -> onEvent(ClassesUiEvent.FilterApplied(filters)) },
                onClassSectionChanged = { selectedLabel ->
                    val scope = classScopeOptions.firstOrNull { it.label == selectedLabel }
                        ?: classScopeOptions.first()
                    onEvent(ClassesUiEvent.FilterClassSectionChanged(scope.className, scope.sectionName))
                },
                onSubjectCodeChanged = { onEvent(ClassesUiEvent.FilterSubjectCodeChanged(it)) },
                onCreatedFromChanged = { onEvent(ClassesUiEvent.FilterCreatedFromChanged(it)) },
                onCreatedToChanged = { onEvent(ClassesUiEvent.FilterCreatedToChanged(it)) },
                subjectCodeOptions = state.subjectSelectionOptions.keys
                    .sortedWith(compareBySmartText<String> { it })
            )
            ClassesDialogMode.EDITOR -> ClassesEditorDialog(
                draft = state.editorDraft,
                availableSubjectOptions = state.subjectSelectionOptions.entries
                    .sortedWith(compareBySmartText<Map.Entry<String, String>> { it.value })
                    .map { it.key to it.value },
                isSaving = state.isSaving,
                apiError = state.apiErrorMessage,
                onDismiss = { if (!state.isSaving) onEvent(ClassesUiEvent.DialogDismissed) },
                onSave = { onEvent(ClassesUiEvent.EditorSaveRequested) },
                onClassChanged = { onEvent(ClassesUiEvent.EditorClassChanged(it)) },
                onSectionChanged = { onEvent(ClassesUiEvent.EditorSectionChanged(it)) },
                onSubjectCodesChanged = { onEvent(ClassesUiEvent.EditorSubjectCodesChanged(it)) }
            )
            ClassesDialogMode.DETAILS -> {
                val row = page.items.firstOrNull { it.id == state.activeClassId }
                if (row != null) {
                    ClassesDetailsDialog(
                        row = row,
                        subjectCodeReference = state.subjectSelectionOptions.entries
                            .sortedWith(compareBySmartText<Map.Entry<String, String>> { it.value })
                            .map { it.key to it.value },
                        onClose = { onEvent(ClassesUiEvent.DialogDismissed) }
                    )
                }
            }
            ClassesDialogMode.DELETE_CONFIRM -> {
                val row = page.items.firstOrNull { it.id == state.activeClassId }
                if (row != null) {
                    AppConfirmationDialog(
                        onDismiss = { onEvent(ClassesUiEvent.DialogDismissed) },
                        title = "Delete Class",
                        subtitle = "This will remove the class section from the current school year.",
                        bodyText = "Delete ${row.className}-${row.sectionName}?",
                        confirmLabel = "Delete",
                        tone = AppConfirmationDialogTone.Danger,
                        confirmEnabled = !state.isSaving,
                        onConfirm = { onEvent(ClassesUiEvent.DeleteConfirmed) }
                    )
                }
            }
            ClassesDialogMode.NONE -> Unit
        }
    }
}
